// Trainer Application — /trainer/apply
//
// Phase 1 frontend only. Authenticated via the EXISTING Supabase
// phone-OTP flow (no separate trainer auth). Submission goes
// exclusively through submit_trainer_application(); no user_id is
// ever sent from the browser — auth.uid() is resolved server-side.
//
// Phone is read-only, sourced from the authenticated session, so a
// trainer can never submit a phone identity other than their own.

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { AppShell } from '../../components/AppShell';
import { BrandButton } from '../../components/BrandButton';
import { useSession } from '../../lib/useSession';
import {
  rpcGetMyTrainerProfile,
  rpcSubmitTrainerApplication,
  rpcGetCurrentTrainerProgramTerms,
  hasTrainerApplication,
  isSubmitSuccessOutcome,
  readTrainerOutcome,
  readTermsBody,
  readTermsTitle,
  readTermsCheckboxLabel,
} from '../../lib/trainerApplication';

const inputStyle = {
  padding: '14px 16px',
  border: '1px solid var(--line)',
  borderRadius: 'var(--radius)',
  background: 'var(--paper)',
  color: 'var(--ink)',
  fontSize: '16px',
  width: '100%',
};
const errStyle = { color: 'var(--danger, #b03a2e)' };

export default function TrainerApply() {
  const nav = useNavigate();
  const { session, loading: sessionLoading, configured } = useSession();

  const [checking, setChecking] = useState(true);
  const [checkError, setCheckError] = useState('');
  const [alreadyApplied, setAlreadyApplied] = useState(false);

  const [termsLoading, setTermsLoading] = useState(true);
  const [termsError, setTermsError] = useState('');
  const [termsBody, setTermsBody] = useState('');
  const [termsTitle, setTermsTitle] = useState('');
  const [termsCheckboxLabel, setTermsCheckboxLabel] = useState('');

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [trainingSpecialty, setTrainingSpecialty] = useState('');
  const [location, setLocation] = useState('');
  const [websiteOrSocial, setWebsiteOrSocial] = useState('');
  const [bio, setBio] = useState('');
  const [certificationCredentials, setCertificationCredentials] = useState('');
  const [approximateActiveClients, setApproximateActiveClients] = useState('');
  const [requestedTrainerCode, setRequestedTrainerCode] = useState('');
  const [termsChecked, setTermsChecked] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formError, setFormError] = useState('');
  const [fieldError, setFieldError] = useState({});

  const phoneDisplay = useMemo(() => {
    const p = session?.user?.phone;
    return p ? (p.startsWith('+') ? p : '+' + p) : '';
  }, [session]);

  // If the trainer already has an application on file, this screen
  // is not re-shown — the dashboard is the single source of truth
  // for status. Backend-driven, not a frontend guess.
  useEffect(() => {
    if (sessionLoading || !session) return;
    let cancelled = false;
    setChecking(true);
    rpcGetMyTrainerProfile().then((res) => {
      if (cancelled) return;
      if (!res.ok) {
        setCheckError(res.error || 'We could not check your trainer status.');
        setChecking(false);
        return;
      }
      if (hasTrainerApplication(res.data)) {
        setAlreadyApplied(true);
      }
      setChecking(false);
    });
    return () => { cancelled = true; };
  }, [sessionLoading, session]);

  useEffect(() => {
    if (sessionLoading || !session) return;
    let cancelled = false;
    setTermsLoading(true);
    rpcGetCurrentTrainerProgramTerms().then((res) => {
      if (cancelled) return;
      if (!res.ok) {
        setTermsError(res.error || 'We could not load the Trainer Program terms.');
        setTermsLoading(false);
        return;
      }
      setTermsBody(readTermsBody(res.data) || '');
      setTermsTitle(readTermsTitle(res.data) || 'Trainer Program Agreement');
      setTermsCheckboxLabel(readTermsCheckboxLabel(res.data) || '');
      setTermsLoading(false);
    });
    return () => { cancelled = true; };
  }, [sessionLoading, session]);

  if (!sessionLoading && !session) {
    return <Navigate to="/sign-in" replace state={{ returnTo: '/trainer/apply' }} />;
  }
  if (alreadyApplied) {
    return <Navigate to="/trainer/dashboard" replace />;
  }

  function validate() {
    const errs = {};
    if (!firstName.trim()) errs.firstName = 'Please enter your first name.';
    if (!lastName.trim()) errs.lastName = 'Please enter your last name.';
    if (!email.trim()) errs.email = 'Please enter your email address.';
    if (!businessName.trim()) errs.businessName = 'Please enter your business name.';
    if (!trainingSpecialty.trim()) errs.trainingSpecialty = 'Please enter your training specialty.';
    if (!location.trim()) errs.location = 'Please enter your location.';
    if (!bio.trim()) errs.bio = 'Please tell us a little about yourself.';
    if (!requestedTrainerCode.trim()) errs.requestedTrainerCode = 'Please request a trainer code.';
    if (!termsChecked) errs.terms = 'Please agree to the Trainer Program Agreement.';
    setFieldError(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleSubmit(e) {
    e?.preventDefault?.();
    setFormError('');
    if (!validate()) return;
    setSubmitting(true);
    const res = await rpcSubmitTrainerApplication({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      email: email.trim(),
      phone: phoneDisplay,
      businessName: businessName.trim(),
      trainingSpecialty: trainingSpecialty.trim(),
      location: location.trim(),
      websiteOrSocial: websiteOrSocial.trim(),
      bio: bio.trim(),
      certificationCredentials: certificationCredentials.trim(),
      approximateActiveClients: approximateActiveClients === '' ? null : Number(approximateActiveClients),
      requestedTrainerCode: requestedTrainerCode.trim(),
      programTermsAccepted: termsChecked,
    });
    setSubmitting(false);
    if (!res.ok) {
      setFormError(res.error || "We couldn't submit your application. Please try again.");
      return;
    }
    const outcome = readTrainerOutcome(res.data);
    if (isSubmitSuccessOutcome(outcome)) {
      setSubmitted(true);
      return;
    }
    setFormError('We could not submit your application right now. Please try again.');
  }

  return (
    <AppShell>
      <div className="wizard-card" data-testid="trainer-apply-page">
        <div className="eyebrow" data-testid="trainer-apply-eyebrow">Trainer Program</div>
        <h1 className="h2 mt-3" data-testid="trainer-apply-title">Apply to become a partner trainer</h1>
        <p className="mt-3" style={{ color: 'var(--text-dim)' }}>
          Tell us about your training business. Our team reviews every application.
        </p>

        {!configured && (
          <div className="mt-6 p-4" style={{ background: 'var(--warning-bg)', border: '1px solid var(--warning)', borderRadius: 'var(--radius)' }}>
            <p className="text-sm" style={errStyle}>Trainer applications are temporarily unavailable.</p>
          </div>
        )}

        {(sessionLoading || checking) && configured && (
          <p className="mt-8 text-sm" style={{ color: 'var(--text-dim)' }} data-testid="trainer-apply-loading">
            Checking your trainer status…
          </p>
        )}

        {checkError && !checking && (
          <div className="mt-6 p-4" style={{ background: 'var(--warning-bg)', border: '1px solid var(--warning)', borderRadius: 'var(--radius)' }} data-testid="trainer-apply-check-error">
            <p className="text-sm" style={errStyle}>{checkError}</p>
            <div className="mt-3">
              <BrandButton type="button" variant="outline" size="sm" onClick={() => window.location.reload()} data-testid="trainer-apply-retry-btn">
                Try again
              </BrandButton>
            </div>
          </div>
        )}

        {!checking && !checkError && !alreadyApplied && configured && session && !submitted && (
          <form onSubmit={handleSubmit} className="mt-8" data-testid="trainer-apply-form" noValidate>
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className="eyebrow inline-block" style={{ color: 'var(--ink)' }}>First name</label>
                <input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)}
                  disabled={submitting} maxLength={60} className="mt-2" style={inputStyle}
                  data-testid="trainer-apply-first-name-input" />
                {fieldError.firstName && <p className="mt-2 text-sm" style={errStyle} data-testid="trainer-apply-first-name-error">{fieldError.firstName}</p>}
              </div>
              <div>
                <label className="eyebrow inline-block" style={{ color: 'var(--ink)' }}>Last name</label>
                <input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)}
                  disabled={submitting} maxLength={60} className="mt-2" style={inputStyle}
                  data-testid="trainer-apply-last-name-input" />
                {fieldError.lastName && <p className="mt-2 text-sm" style={errStyle} data-testid="trainer-apply-last-name-error">{fieldError.lastName}</p>}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-6 mt-6">
              <div>
                <label className="eyebrow inline-block" style={{ color: 'var(--ink)' }}>Email</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  disabled={submitting} maxLength={254} className="mt-2" style={inputStyle}
                  data-testid="trainer-apply-email-input" />
                {fieldError.email && <p className="mt-2 text-sm" style={errStyle} data-testid="trainer-apply-email-error">{fieldError.email}</p>}
              </div>
              <div>
                <label className="eyebrow inline-block" style={{ color: 'var(--ink)' }}>Phone (on file from sign-in)</label>
                <input type="tel" value={phoneDisplay || '—'} readOnly className="mt-2"
                  style={{ ...inputStyle, background: 'var(--paper-2, rgba(0,0,0,0.03))', color: 'var(--ink-2)' }}
                  data-testid="trainer-apply-phone-input" />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-6 mt-6">
              <div>
                <label className="eyebrow inline-block" style={{ color: 'var(--ink)' }}>Business name</label>
                <input type="text" value={businessName} onChange={(e) => setBusinessName(e.target.value)}
                  disabled={submitting} maxLength={120} className="mt-2" style={inputStyle}
                  data-testid="trainer-apply-business-name-input" />
                {fieldError.businessName && <p className="mt-2 text-sm" style={errStyle} data-testid="trainer-apply-business-name-error">{fieldError.businessName}</p>}
              </div>
              <div>
                <label className="eyebrow inline-block" style={{ color: 'var(--ink)' }}>Training specialty</label>
                <input type="text" value={trainingSpecialty} onChange={(e) => setTrainingSpecialty(e.target.value)}
                  disabled={submitting} maxLength={120} className="mt-2" style={inputStyle}
                  data-testid="trainer-apply-specialty-input" />
                {fieldError.trainingSpecialty && <p className="mt-2 text-sm" style={errStyle} data-testid="trainer-apply-specialty-error">{fieldError.trainingSpecialty}</p>}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-6 mt-6">
              <div>
                <label className="eyebrow inline-block" style={{ color: 'var(--ink)' }}>Location</label>
                <input type="text" value={location} onChange={(e) => setLocation(e.target.value)}
                  disabled={submitting} maxLength={120} className="mt-2" style={inputStyle}
                  data-testid="trainer-apply-location-input" />
                {fieldError.location && <p className="mt-2 text-sm" style={errStyle} data-testid="trainer-apply-location-error">{fieldError.location}</p>}
              </div>
              <div>
                <label className="eyebrow inline-block" style={{ color: 'var(--ink)' }}>Website or social (optional)</label>
                <input type="text" value={websiteOrSocial} onChange={(e) => setWebsiteOrSocial(e.target.value)}
                  disabled={submitting} maxLength={200} className="mt-2" style={inputStyle}
                  data-testid="trainer-apply-website-input" />
              </div>
            </div>

            <div className="mt-6">
              <label className="eyebrow inline-block" style={{ color: 'var(--ink)' }}>Bio</label>
              <textarea value={bio} onChange={(e) => setBio(e.target.value)}
                disabled={submitting} maxLength={2000} rows={4} className="mt-2"
                style={{ ...inputStyle, resize: 'vertical' }}
                data-testid="trainer-apply-bio-input" />
              {fieldError.bio && <p className="mt-2 text-sm" style={errStyle} data-testid="trainer-apply-bio-error">{fieldError.bio}</p>}
            </div>

            <div className="grid sm:grid-cols-2 gap-6 mt-6">
              <div>
                <label className="eyebrow inline-block" style={{ color: 'var(--ink)' }}>Certification credentials (optional)</label>
                <input type="text" value={certificationCredentials} onChange={(e) => setCertificationCredentials(e.target.value)}
                  disabled={submitting} maxLength={200} className="mt-2" style={inputStyle}
                  data-testid="trainer-apply-certifications-input" />
              </div>
              <div>
                <label className="eyebrow inline-block" style={{ color: 'var(--ink)' }}>Approximate active clients (optional)</label>
                <input type="number" min="0" inputMode="numeric" value={approximateActiveClients}
                  onChange={(e) => setApproximateActiveClients(e.target.value)}
                  disabled={submitting} className="mt-2" style={inputStyle}
                  data-testid="trainer-apply-active-clients-input" />
              </div>
            </div>

            <div className="mt-6">
              <label className="eyebrow inline-block" style={{ color: 'var(--ink)' }}>Requested trainer code</label>
              <input type="text" value={requestedTrainerCode}
                onChange={(e) => setRequestedTrainerCode(e.target.value.toUpperCase())}
                disabled={submitting} maxLength={40} className="mt-2"
                style={{ ...inputStyle, textTransform: 'uppercase' }}
                data-testid="trainer-apply-requested-code-input" />
              <p className="mt-2 text-xs" style={{ color: 'var(--text-dim)' }}>
                Final approval and activation of your code is decided by Eat This Fast &amp; Fresh.
              </p>
              {fieldError.requestedTrainerCode && <p className="mt-2 text-sm" style={errStyle} data-testid="trainer-apply-requested-code-error">{fieldError.requestedTrainerCode}</p>}
            </div>

            {/* Trainer Program Terms — authoritative source: get_current_trainer_program_terms() */}
            <div className="mt-8">
              <div className="eyebrow" style={{ color: 'var(--text-dim)' }}>{termsTitle || 'Trainer Program Agreement'}</div>
              <div className="mt-3 p-3" style={{
                border: '1px solid var(--line)', borderRadius: 'var(--radius)', background: 'var(--paper)',
                maxHeight: 260, overflow: 'auto', whiteSpace: 'pre-wrap', fontSize: '0.9rem', color: 'var(--ink-2)',
              }} data-testid="trainer-apply-terms-text">
                {termsLoading ? 'Loading terms…' : (termsBody || 'Terms are unavailable right now.')}
              </div>
              {termsError && !termsLoading && (
                <p className="mt-2 text-sm" style={errStyle} data-testid="trainer-apply-terms-error">{termsError}</p>
              )}
              <label className="mt-3 flex items-start gap-2 text-sm" style={{ color: 'var(--ink)' }}>
                <input type="checkbox" checked={termsChecked} disabled={submitting || termsLoading || !termsBody}
                  onChange={(e) => setTermsChecked(e.target.checked)}
                  data-testid="trainer-apply-terms-checkbox" />
                <span>{termsCheckboxLabel || 'I have reviewed and agree to the Trainer Program Agreement.'}</span>
              </label>
              {fieldError.terms && <p className="mt-2 text-sm" style={errStyle} data-testid="trainer-apply-terms-checkbox-error">{fieldError.terms}</p>}
            </div>

            {formError && <p className="mt-6 text-sm" style={errStyle} data-testid="trainer-apply-form-error">{formError}</p>}

            <div className="mt-8">
              <BrandButton type="submit" variant="primary" disabled={submitting} data-testid="trainer-apply-submit-btn">
                {submitting ? 'Submitting…' : 'Submit application'}
              </BrandButton>
            </div>
          </form>
        )}

        {submitted && (
          <div className="mt-8" data-testid="trainer-apply-confirmation">
            <div className="eyebrow" style={{ color: 'var(--success, #2e7d32)' }} data-testid="trainer-apply-confirmation-eyebrow">
              Application submitted
            </div>
            <p className="mt-4" style={{ color: 'var(--ink)' }} data-testid="trainer-apply-confirmation-message">
              Your trainer application has been submitted to Eat This Fast &amp; Fresh for review.
            </p>
            <p className="mt-2" style={{ color: 'var(--text-dim)' }} data-testid="trainer-apply-confirmation-followup">
              You will receive an email when your application status changes.
            </p>
            <div className="mt-8">
              <BrandButton type="button" variant="primary" onClick={() => nav('/trainer/dashboard', { replace: true })} data-testid="trainer-apply-view-dashboard-btn">
                View Trainer Dashboard
              </BrandButton>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}
